import classNames from "classnames";
import isUndefined from "lodash/isUndefined";
import { ErrorMessage } from "PFComponents/error_message";
import { Label } from "PFComponents/label";
import { Id } from "PFTypes";
import { AriaAttributes, useState } from "react";

import { Radio, RadioItem } from "./radio";
import css from "./radios.module.scss";

type Props<ID_TYPE extends Id> = {
  items: RadioItem<ID_TYPE>[];
  defaultValue?: ID_TYPE;
  value?: ID_TYPE;
  onChange: (id: ID_TYPE) => void;
  inline?: boolean;
  errorMessage?: string;
  required?: boolean;
  label?: string;
  verticalItems?: boolean;
  classes?: {
    root?: string;
    radioGroup?: string;
  };
  name: string;
  disabled?: boolean;
  withRadioMargin?: boolean;
} & AriaAttributes;

export const Radios = <ID_TYPE extends Id = string>({
  defaultValue,
  value,
  items,
  inline,
  onChange,
  errorMessage,
  required,
  label,
  verticalItems,
  classes,
  name,
  disabled,
  withRadioMargin,
  "aria-label": ariaLabel
}: Props<ID_TYPE>) => {
  const [selectedOption, setSelectedOption] = useState<ID_TYPE | undefined>(defaultValue);

  const handleOnClick = (id: ID_TYPE) => {
    setSelectedOption(id);
    onChange(id);
  };

  return (
    <div className={classNames(css.root, classes?.root)}>
      <Label text={label} required={required} className={css.label} variant="bodyRegular" withMargin />
      <fieldset
        className={classNames(css.radioGroup, classes?.radioGroup)}
        style={{
          flexDirection: inline ? "row" : "column"
        }}
        role="group"
        aria-label={ariaLabel || label}
      >
        {items.map((item) => {
          const isChecked = (isUndefined(defaultValue) ? value : selectedOption) === item.id;

          return (
            <Radio
              key={`${item.id}`}
              item={item}
              checked={isChecked}
              onChange={handleOnClick}
              verticalItems={verticalItems}
              name={name}
              disabled={item.disabled || disabled}
              withMargin={withRadioMargin}
            />
          );
        })}
      </fieldset>

      {errorMessage && <ErrorMessage iconSize="sm" message={errorMessage} className={css.errorMessage} />}
    </div>
  );
};
